'use client';

import { InlineAlert, InlineAlertTitle } from '@/components/alert';
import { Button } from '@/components/button';
import HeadlineField from '@/components/customFields/HeadlineField';
import { HeadlineTag } from '@/components/customFields/types';
import IconSelectCalculator from '@/components/iconSelect/IconSelectCalculator';
import { RichText } from '@/components/richText/RichText';
import { TextField } from '@/components/textField';
import { ProductCategory } from '@/helper/enums';
import tracking from '@/helper/tracking';
import type {
  GlobalElectricityConsumptionCalculatorStoryblok,
  PartialStepSettingStoryblok,
} from '@/types/component-types-sb';
import { storyblokEditable } from '@storyblok/react/rsc';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import classes from './ElectricityConsumptionCalculator.module.scss';

type HomeOrFlat = 'home' | 'flat' | '';
type WaterHeating = 'electricity' | 'noelectricity' | '';
const ElectricityConsumptionCalculator: React.FC<{
  blok: GlobalElectricityConsumptionCalculatorStoryblok;
  anchor?: string;
}> = ({ blok, anchor }) => {
  const [selectedHome, setSelectedHome] = useState<HomeOrFlat>('');
  const [selectedPersons, setSelectedPersons] = useState<PartialStepSettingStoryblok>();
  const [selectedPersonsError, setSelectedPersonsError] = useState<boolean>(false);
  const [selectedWaterHeating, setSelectedWaterHeating] = useState<WaterHeating>('');
  const [electricityConsumption, setElectricityConsumption] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [electricityAmountGreen, setElectricityAmountGreen] = useState<number>(0);
  const [electricityAmountYellow, setElectricityAmountYellow] = useState<number>(0);
  const { t } = useTranslation();

  function onCalcSubmit() {
    if (!selectedPersons) {
      setSelectedPersonsError(true);
      return;
    }

    const selectedOption =
      blok.config?.[0]?.[selectedHome]?.[0]?.[selectedWaterHeating]?.[Number(selectedPersons.key) - 1];
    setElectricityAmountGreen(selectedOption.green);
    setElectricityAmountYellow(selectedOption.yellow);
    setShowResult(true);

    // Tracking
    let result = 'red';
    if (electricityConsumption <= selectedOption.green) {
      result = 'green';
    } else if (electricityConsumption <= selectedOption.yellow) {
      result = 'yellow';
    }
    tracking.calculatorEcoPower({
      amountResidents: String(selectedOption.person_amount),
      consumption: String(electricityConsumption),
      livingSituation: selectedHome === 'home' ? 'one- or two-family house' : 'apartment',
      result,
    });
  }

  useEffect(() => {
    if (selectedPersons && selectedHome && selectedWaterHeating) {
      const expectedAmountGreen =
        blok.config?.[0]?.[selectedHome]?.[0]?.[selectedWaterHeating]?.[Number(selectedPersons.key) - 1]?.green;
      setElectricityConsumption(Number(expectedAmountGreen) ?? 0);
    }
  }, [selectedPersons, selectedHome, selectedWaterHeating, blok.config]);

  useEffect(() => {
    if (selectedPersons) {
      setSelectedPersonsError(false);
    }
  }, [selectedPersons]);

  return (
    <div id={anchor || undefined} className={classes.electricityConsumptionCalculator}>
      <HeadlineField className={classes.heading} tag={HeadlineTag.H2} style={HeadlineTag.H3}>
        {blok.headline}
      </HeadlineField>
      <form
        className={classes.electricityConsumptionCalculatorForm}
        onSubmit={(e) => {
          e.preventDefault();
        }}
        {...storyblokEditable(blok)}
        data-testid="electricity-consumption-calculator-form"
      >
        <div className={classes.formField}>
          <label>{blok.label_house}*</label>
          <div
            role="group"
            aria-label={t('electricityConsumptionCalculator.living') as string}
            className={classes.toggleButtons}
          >
            <button
              type="button"
              data-testid="electricity-consumption-calculator-form-living-home-button"
              className={classNames(classes.toggleButton, selectedHome === 'home' && classes.toggleButtonSelected)}
              onClick={() => {
                setSelectedHome('home');
                setShowResult(false);
              }}
            >
              {blok.option_house}
            </button>
            <button
              type="button"
              data-testid="electricity-consumption-calculator-form-living-flat-button"
              className={classNames(classes.toggleButton, selectedHome === 'flat' && classes.toggleButtonSelected)}
              onClick={() => {
                setSelectedHome('flat');
                setShowResult(false);
              }}
            >
              {blok.option_apartment}
            </button>
          </div>
        </div>
        <div className={classNames(classes.formField, classes.iconSelect)}>
          <label>{blok.label_persons}*</label>
          <IconSelectCalculator
            selected={selectedPersons}
            type={ProductCategory.Electricity}
            setSelectedStep={setSelectedPersons}
            initiallyUnset
            error={selectedPersonsError}
          />
        </div>
        <div className={classes.formField}>
          <label>{blok.label_water}*</label>
          <div
            role="group"
            aria-label={t('electricityConsumptionCalculator.heating') as string}
            className={classes.toggleButtons}
          >
            <button
              type="button"
              data-testid="electricity-consumption-calculator-form-heating-noElectricity-button"
              className={classNames(
                classes.toggleButton,
                selectedWaterHeating === 'noelectricity' && classes.toggleButtonSelected
              )}
              onClick={() => {
                setSelectedWaterHeating('noelectricity');
                setShowResult(false);
              }}
            >
              {blok.option_no_electricity}
            </button>
            <button
              type="button"
              data-testid="electricity-consumption-calculator-form-heating-electricity-button"
              className={classNames(
                classes.toggleButton,
                selectedWaterHeating === 'electricity' && classes.toggleButtonSelected
              )}
              onClick={() => {
                setSelectedWaterHeating('electricity');
                setShowResult(false);
              }}
            >
              {blok.option_electricity}
            </button>
          </div>
        </div>
        <div className={classes.formField}>
          <label>{blok.label_consumption}*</label>
          <TextField
            type="text"
            size="small"
            inputMode="numeric"
            label={t('Jahresverbrauch')}
            required
            value={electricityConsumption === 0 ? '' : electricityConsumption}
            InputProps={{
              endAdornment: <span aria-hidden={true}>{t('calculator.suffixConsumption')}</span>,
              inputProps: {
                min: 0,
                'aria-label': 'Kilowattstunde',
                'data-testid': 'electricity-consumption-calculator-form-consumption-input',
              },
            }}
            onChange={(e) => {
              const value = Number(e.target.value.trim()) || 0;
              setElectricityConsumption(value);
              setShowResult(false);
            }}
          />
        </div>

        {showResult && electricityConsumption <= electricityAmountGreen && (
          <InlineAlert severity="success" variant="standard">
            <InlineAlertTitle>{blok.success_headline}</InlineAlertTitle>
            <RichText text={blok.success_text} />
          </InlineAlert>
        )}

        {showResult &&
          electricityConsumption > electricityAmountGreen &&
          electricityConsumption <= electricityAmountYellow && (
            <InlineAlert severity="warning" variant="standard">
              <InlineAlertTitle>{blok.warning_headline}</InlineAlertTitle>
              <RichText text={blok.warning_text} />
            </InlineAlert>
          )}

        {showResult && electricityConsumption > electricityAmountYellow && (
          <InlineAlert severity="error" variant="standard">
            <InlineAlertTitle>{blok.error_headline}</InlineAlertTitle>
            <RichText text={blok.error_text} />
          </InlineAlert>
        )}

        {!showResult && (
          <Button
            variant="ctaFull"
            fullWidth
            onClick={onCalcSubmit}
            disabled={!selectedWaterHeating || !selectedHome || !electricityConsumption}
            data-testid="electricity-consumption-calculator-form-submit-button"
          >
            {blok.label_cta}
          </Button>
        )}
      </form>
    </div>
  );
};

export default ElectricityConsumptionCalculator;
